import React, { useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { deviceHasAiBoard, deviceAiBoardMac } from '../utils/deviceLampActions';

/** Power-cycles the AI board attached to a jnet2 (GPIO 456), with a confirm step. */
const RebootAiBoardButton = ({ device, sx }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  if (!deviceHasAiBoard(device)) return null;

  const serial = device.serial_number;
  const aiMac = deviceAiBoardMac(device);

  const send = async () => {
    setSending(true);
    try {
      await api.rebootAiBoard(serial);
      enqueueSnackbar(`AI board reboot sent to ${serial}`, { variant: 'success' });
      setConfirmOpen(false);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to reboot AI board', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Tooltip title={device.connected ? 'Power-cycle the attached AI board (GPIO 456)' : 'Device is offline'}>
        <span style={{ display: 'inline-flex' }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={!device.connected || sending}
            startIcon={<RestartAltIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', py: 0.5, px: 1.5, ...sx }}
          >
            Reboot AI
          </Button>
        </span>
      </Tooltip>
      <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reboot AI board?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cuts power to the AI board{aiMac ? ` ${aiMac}` : ''} attached to {serial} for 5 seconds
            (GPIO 456), then powers it back on. The AI board is unavailable until it finishes booting.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={send}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <RestartAltIcon />}
          >
            Reboot AI
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RebootAiBoardButton;
